import type { ChallengeInfo } from '@cruz/shared';
import { useEffect, useState } from 'react';
import { remainingMs } from './clock.ts';
import { Notice, primaryButton, ShareButtons } from './ui.tsx';

/** In the room the list of friends refreshes quickly: people arrive one after another. */
export const ROOM_POLL_MS = 2000;

/** Who is in the room, as chips; the creator first, and "tú" for this phone. */
export function RoomList({ room, creator, me }: { room: string[]; creator: string; me?: string }) {
  return (
    <section
      aria-labelledby="room-title"
      className="rounded-xl border border-gold/60 bg-cream-200 p-4 dark:border-gold/30 dark:bg-night-800"
    >
      <h2 id="room-title" className="font-serif text-2xl leading-tight font-semibold">
        En esta sala
        <span className="tabular ml-2 font-sans text-base font-medium text-ink-soft dark:text-cream/70">
          {room.length} {room.length === 1 ? 'persona' : 'personas'}
        </span>
      </h2>
      <ul className="mt-3 flex flex-wrap gap-2" aria-live="polite">
        {room.map((name) => (
          <li
            key={name}
            className="animate-rise rounded-full border border-purple/20 bg-paper px-3 py-1.5 text-base text-ink dark:border-cream/15 dark:bg-night-700 dark:text-cream"
          >
            {name}
            {name === creator && (
              <span className="ml-1.5 text-sm text-gold-700 dark:text-gold-300">(organiza)</span>
            )}
            {name === me && name !== creator && (
              <span className="ml-1.5 text-sm text-ink-soft dark:text-cream/70">(tú)</span>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

/** A soft pulse with a sentence: the room is alive and waiting. */
function Waiting({ children }: { children: string }) {
  return (
    <p
      role="status"
      className="flex items-center gap-2.5 text-base text-ink-soft dark:text-cream/75"
    >
      <span className="flex gap-1" aria-hidden>
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="animate-dot h-1.5 w-1.5 rounded-full bg-gold"
            style={{ animationDelay: `${i * 160}ms` }}
          />
        ))}
      </span>
      {children}
    </p>
  );
}

/**
 * The waiting room of a live challenge. The creator shares the link and starts the game
 * for everyone; the others see who has arrived until it starts.
 */
export function LiveRoom({
  info,
  code,
  me,
  onStart,
}: {
  info: ChallengeInfo;
  code: string;
  me: string;
  onStart: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isCreator = info.me?.is_creator === true;
  const alone = info.room.length < 2;

  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      await onStart();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Algo ha fallado. Inténtalo de nuevo.');
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <RoomList room={info.room} creator={info.creator} me={me} />
      {isCreator ? (
        <>
          <section className="space-y-3">
            <h2 className="font-serif text-2xl">Invita a tus amigos</h2>
            <ShareButtons code={code} creator={info.creator} />
          </section>
          <section className="space-y-2">
            {error && <Notice tone="error">{error}</Notice>}
            <button
              type="button"
              className={primaryButton}
              disabled={busy}
              onClick={() => void start()}
            >
              {busy ? 'Empezando…' : 'Empezar para todos'}
            </button>
            <p className="text-center text-sm text-ink-soft dark:text-cream/70">
              {alone
                ? 'Aún no ha entrado nadie más. Quien llegue tarde podrá jugar igualmente.'
                : 'La primera pregunta saldrá a la vez en todos los móviles.'}
            </p>
          </section>
        </>
      ) : (
        <Waiting>{`Esperando a que ${info.creator} empiece…`}</Waiting>
      )}
    </div>
  );
}

/** "3, 2, 1…" on the server's clock, so every phone reaches zero together. */
export function Countdown({
  startsAt,
  offset,
  onDone,
}: {
  startsAt: string;
  offset: number;
  onDone: () => void;
}) {
  const [left, setLeft] = useState(() => remainingMs(startsAt, offset));

  useEffect(() => {
    if (left <= 0) {
      onDone();
      return;
    }
    const t = setTimeout(() => setLeft(remainingMs(startsAt, offset)), Math.min(left, 200));
    return () => clearTimeout(t);
  }, [left, startsAt, offset, onDone]);

  const n = Math.ceil(left / 1000);
  return (
    <div role="timer" aria-live="assertive" className="py-16 text-center">
      <p className="text-lg text-ink-soft dark:text-cream/75">¡Preparados!</p>
      <p
        key={n}
        className="animate-rise tabular mt-2 font-serif text-8xl leading-none font-semibold text-purple dark:text-gold-300"
      >
        {n > 0 ? n : '¡Ya!'}
      </p>
    </div>
  );
}
